import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { cardSx, hubGhostBtnSx } from './accountHubStyles';
import { extractApiError } from './accountHubUtils';
import UlcmContactService from 'src/services/UlcmContact.service';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber';
import ContactRow from './ContactRow';
import AddContactDialog from './AddContactDialog';
import {
  canRemoveContact,
  contactCopy,
  contactError,
  type AddState,
  type ContactRowData,
  type ContactType,
  type DeleteTarget,
  type EditingState,
  type PendingProposal,
} from './contactInfo';

export interface ContactSectionHandle {
  promotePrimary: (id: number, oldPrimaryId: number | null) => void;
}

interface ContactSectionProps {
  type: ContactType;
  rows: ContactRowData[];
  asUser: string;
  /** First name of the contact owner when editing another person's contacts. */
  ownerName?: string;
  crossTypeCount: number;
  refetch: () => Promise<void>;
  onProposalCreated: (proposal: PendingProposal) => void;
  onRemoveRequest: (target: DeleteTarget) => void;
}

const ContactSection = forwardRef<ContactSectionHandle, ContactSectionProps>(
  (
    {
      type,
      rows,
      asUser,
      ownerName,
      crossTypeCount,
      refetch,
      onProposalCreated,
      onRemoveRequest,
    },
    ref
  ) => {
    const copy = contactCopy[type];

    const [glowId, setGlowId] = useState<number | null>(null);
    const [fadeId, setFadeId] = useState<number | null>(null);
    const [editing, setEditing] = useState<EditingState>({
      id: null,
      value: '',
      error: null,
    });
    const [add, setAdd] = useState<AddState>({
      open: false,
      value: '',
      saving: false,
      error: null,
    });
    const [setPrimaryLoadingId, setSetPrimaryLoadingId] = useState<
      number | null
    >(null);
    const [setPrimaryError, setSetPrimaryError] = useState<{
      id: number;
      detail: string;
    } | null>(null);

    useEffect(() => {
      if (fadeId == null) {
        return;
      }
      const timer = setTimeout(() => setFadeId(null), 1100);
      return () => clearTimeout(timer);
    }, [fadeId]);

    const handleGlowEnd = useCallback((id: number): void => {
      setGlowId((current) => (current === id ? null : current));
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        promotePrimary: (id: number, oldPrimaryId: number | null): void => {
          setGlowId(id);
          if (oldPrimaryId != null && oldPrimaryId !== id) {
            setFadeId(oldPrimaryId);
          }
        },
      }),
      []
    );

    const handleSetPrimary = useCallback(
      async (id: number): Promise<void> => {
        const oldPrimaryId = rows.find((r) => r.isPrimary)?.id ?? null;
        setSetPrimaryLoadingId(id);
        setSetPrimaryError(null);
        try {
          if (type === 'email') {
            await UlcmContactService.setPrimaryEmail(id, asUser);
          } else {
            await UlcmContactService.setPrimaryPhone(id, asUser);
          }
          setGlowId(id);
          if (oldPrimaryId != null && oldPrimaryId !== id) {
            setFadeId(oldPrimaryId);
          }
          await refetch();
        } catch (err: unknown) {
          setSetPrimaryError({
            id,
            detail: extractApiError(err, contactError(type, 'set primary')),
          });
        } finally {
          setSetPrimaryLoadingId(null);
        }
      },
      [type, rows, refetch, asUser]
    );

    const handleEditStart = useCallback((id: number, value: string): void => {
      setEditing({ id, value, error: null });
    }, []);

    const handleEditCancel = useCallback((): void => {
      setEditing({ id: null, value: '', error: null });
    }, []);

    const handleEditValueChange = useCallback(
      (value: string): void => {
        setEditing((prev) => {
          const next =
            type === 'phone'
              ? formatPhoneNumber(value, prev.value) ?? value
              : value;
          return { ...prev, value: next };
        });
      },
      [type]
    );

    const handleEditSave = useCallback(
      async (id: number): Promise<void> => {
        const value = editing.value;
        try {
          const result =
            type === 'email'
              ? await UlcmContactService.proposeEmail(
                  value.trim(),
                  undefined,
                  asUser,
                  id
                )
              : await UlcmContactService.proposePhone(
                  value.trim(),
                  undefined,
                  asUser,
                  id
                );
          setEditing({ id: null, value: '', error: null });
          onProposalCreated({
            otp_id: result.otp_id,
            contact_type: type,
            contact_value: value.trim(),
            replace_id: id,
          });
        } catch (err: unknown) {
          setEditing((prev) => ({
            ...prev,
            error: extractApiError(err, contactError(type, 'update')),
          }));
        }
      },
      [type, editing.value, asUser, onProposalCreated]
    );

    const openAdd = useCallback((): void => {
      setAdd((prev) => ({ ...prev, value: '', error: null, open: true }));
    }, []);

    const closeAdd = useCallback((): void => {
      setAdd((prev) => ({ ...prev, open: false }));
    }, []);

    const handleAddValueChange = useCallback(
      (value: string): void => {
        setAdd((prev) => {
          const next =
            type === 'phone'
              ? formatPhoneNumber(value, prev.value) ?? value
              : value;
          return { ...prev, value: next };
        });
      },
      [type]
    );

    const handleAdd = useCallback(async (): Promise<void> => {
      const value = add.value;
      if (!value.trim()) {
        return;
      }
      setAdd((prev) => ({ ...prev, saving: true, error: null }));
      try {
        const result =
          type === 'email'
            ? await UlcmContactService.proposeEmail(
                value.trim(),
                undefined,
                asUser
              )
            : await UlcmContactService.proposePhone(
                value.trim(),
                undefined,
                asUser
              );
        setAdd((prev) => ({ ...prev, open: false, value: '' }));
        onProposalCreated({
          otp_id: result.otp_id,
          contact_type: type,
          contact_value: value.trim(),
          replace_id: null,
        });
      } catch (err: unknown) {
        setAdd((prev) => ({
          ...prev,
          error: extractApiError(err, contactError(type, 'add')),
        }));
      } finally {
        setAdd((prev) => ({ ...prev, saving: false }));
      }
    }, [type, add.value, asUser, onProposalCreated]);

    const handleRemove = useCallback(
      (id: number, value: string): void => {
        onRemoveRequest({ type, id, value });
      },
      [type, onRemoveRequest]
    );

    return (
      <>
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
            {copy.title}
          </Typography>
          <Stack gap={0}>
            {rows.map((row, i) => (
              <ContactRow
                key={row.id}
                type={type}
                row={row}
                index={i}
                ownerName={ownerName}
                glowId={glowId}
                fadeId={fadeId}
                editingId={editing.id}
                editingValue={editing.value}
                editingError={editing.error}
                setPrimaryLoadingId={setPrimaryLoadingId}
                setPrimaryError={setPrimaryError}
                canRemove={canRemoveContact(row, rows, crossTypeCount)}
                onSetPrimary={(id) => void handleSetPrimary(id)}
                onEditStart={handleEditStart}
                onEditCancel={handleEditCancel}
                onEditValueChange={handleEditValueChange}
                onEditSave={(id) => void handleEditSave(id)}
                onRemove={handleRemove}
                onGlowEnd={handleGlowEnd}
              />
            ))}
            {rows.length === 0 && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {copy.emptyText}
              </Typography>
            )}
            {rows.length > 0 && <Divider sx={{ my: 1.5 }} />}
            <Button
              variant="contained"
              disableElevation
              fullWidth
              disabled={setPrimaryLoadingId !== null}
              sx={hubGhostBtnSx}
              onClick={openAdd}
            >
              + Add {copy.addButton}
            </Button>
          </Stack>
        </Box>
        <AddContactDialog
          type={type}
          open={add.open}
          value={add.value}
          saving={add.saving}
          error={add.error}
          ownerName={ownerName}
          onClose={closeAdd}
          onValueChange={handleAddValueChange}
          onSubmit={() => void handleAdd()}
        />
      </>
    );
  }
);

ContactSection.displayName = 'ContactSection';

export default ContactSection;
